import { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export function ThemeProvider({ children }) {
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('admin-theme');
    return saved ? saved === 'dark' : true; // Default to dark
  });

  useEffect(() => {
    localStorage.setItem('admin-theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  const toggleTheme = () => setIsDark(!isDark);

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

// Theme colors
export const themes = {
  dark: {
    // Backgrounds
    bgPrimary: 'bg-gradient-to-br from-[#0a0a0a] via-[#1a1a2e] to-[#16213e]',
    bgCard: 'bg-white/5 backdrop-blur-xl',
    bgCardHover: 'hover:bg-white/10',
    bgInput: 'bg-white/5',
    bgModal: 'bg-gradient-to-br from-[#1a1a2e] to-[#16213e]',
    // Borders
    border: 'border-white/10',
    borderHover: 'hover:border-white/30',
    // Text
    textPrimary: 'text-white',
    textSecondary: 'text-white/60',
    textMuted: 'text-white/40',
    textPlaceholder: 'placeholder-white/30',
    // Header
    headerBg: 'bg-black/20 backdrop-blur-xl border-b border-white/10',
    // Glow effects
    glowGold: 'bg-[#C5A059]/10',
    glowRed: 'bg-[#5D1822]/20',
  },
  light: {
    // Backgrounds
    bgPrimary: 'bg-gradient-to-br from-[#F9F7F2] via-[#FFFFFF] to-[#F5F3EE]',
    bgCard: 'bg-white shadow-lg shadow-black/5',
    bgCardHover: 'hover:bg-gray-50',
    bgInput: 'bg-gray-50',
    bgModal: 'bg-white',
    // Borders
    border: 'border-gray-200',
    borderHover: 'hover:border-gray-300',
    // Text
    textPrimary: 'text-gray-900',
    textSecondary: 'text-gray-600',
    textMuted: 'text-gray-400',
    textPlaceholder: 'placeholder-gray-400',
    // Header
    headerBg: 'bg-white/80 backdrop-blur-xl border-b border-gray-200',
    // Glow effects (softer for light mode)
    glowGold: 'bg-[#C5A059]/5',
    glowRed: 'bg-[#5D1822]/5',
  }
};

export function getTheme(isDark) {
  return isDark ? themes.dark : themes.light;
}
